import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { ensureCartDrawerScroll } from '@theme/section-renderer';

/**
 * Cart drawer.
 *
 * Rules that keep this stable:
 * - Never close()+showModal() while open (replays slide-in = "2nd drawer").
 * - Never set display/animation on the dialog from scroll helpers.
 * - Close with dialog.close() immediately, then unlock — don't wait on animations
 *   (a hung animationend left the modal backdrop blocking the whole site).
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #scrollY = 0;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #readyTimer = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #scrollTimer = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('cart:update', this.#onCartDomChanged);
    document.addEventListener('cart:sections-updated', this.#onCartDomChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('cart:update', this.#onCartDomChanged);
    document.removeEventListener('cart:sections-updated', this.#onCartDomChanged);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleEscapeKey);
    this.#clearReadyTimer();
    this.#clearScrollTimer();
    this.#unlockPageScroll();
  }

  #clearReadyTimer() {
    if (this.#readyTimer !== null) {
      clearTimeout(this.#readyTimer);
      this.#readyTimer = null;
    }
  }

  #clearScrollTimer() {
    if (this.#scrollTimer !== null) {
      clearTimeout(this.#scrollTimer);
      this.#scrollTimer = null;
    }
  }

  /**
   * After qty add/remove the cart HTML is replaced and .cart-drawer__items
   * collapses to 0 height. Re-measure several times (brush rule lands ~50–400ms later).
   */
  #onCartDomChanged = () => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    this.#lockPageScroll();

    const run = () => {
      if (dialog.open) ensureCartDrawerScroll();
    };

    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });

    this.#clearScrollTimer();
    this.#scrollTimer = setTimeout(() => {
      this.#scrollTimer = null;
      run();
    }, 450);
  };

  #lockPageScroll() {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    if (!document.documentElement.hasAttribute('scroll-lock')) {
      this.#scrollY = window.scrollY;
    }

    document.documentElement.setAttribute('scroll-lock', '');
    document.body.classList.add('cart-drawer-open');
    document.body.style.setProperty('--cart-drawer-scroll-y', `-${this.#scrollY}px`);
  }

  #unlockPageScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.removeProperty('--cart-drawer-scroll-y');

    const scrollY = this.#scrollY;
    this.#scrollY = 0;
    window.scrollTo(0, scrollY);
  }

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;
    if (this.refs.dialog?.open) return;
    queueMicrotask(() => this.showDialog());
  };

  #handleOutsideClick = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.open) return;

    if (event.target instanceof Element) {
      const otherDialog = event.target.closest('dialog');
      if (otherDialog && otherDialog !== dialog) return;
    }

    const rect = dialog.getBoundingClientRect();
    const isOutside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (isOutside) this.closeDialog();
  };

  #handleEscapeKey = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#clearReadyTimer();
    dialog.classList.remove('cart-drawer--ready', 'dialog-closing');
    dialog.showModal();
    this.#lockPageScroll();
    this.dispatchEvent(new DialogOpenEvent());

    // Freeze open animation only after it has time to finish — not on the next tick.
    this.#readyTimer = setTimeout(() => {
      this.#readyTimer = null;
      if (dialog.open) dialog.classList.add('cart-drawer--ready');
    }, 350);

    setTimeout(() => {
      if (!dialog.open) return;
      document.addEventListener('click', this.#handleOutsideClick);
      document.addEventListener('keydown', this.#handleEscapeKey);
      ensureCartDrawerScroll();
    });
  }

  closeDialog = () => {
    const { dialog } = this.refs;

    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleEscapeKey);
    this.#clearReadyTimer();
    this.#clearScrollTimer();

    if (dialog?.open) {
      try {
        dialog.close();
      } catch (_) {}
    }

    dialog?.classList.remove('dialog-closing', 'cart-drawer--ready');
    this.#unlockPageScroll();
    this.dispatchEvent(new DialogCloseEvent());
  };

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
